import { type Form } from '@wix/ambassador-forms-v4-form/types';
import { getSortedFields, useFocusedField } from '@wix/form-fields';
import { type ValidationError } from '@wix/form-validator';
import { useCallback } from 'react';

interface ScrollToFirstErrorProps {
  form: Form;
  handleStepChange: (step: number) => void;
  currentStepId?: string;
}

export const useScrollToFirstError = ({
  form,
  handleStepChange,
  currentStepId,
}: ScrollToFirstErrorProps): {
  scrollToFirstError: (errors: ValidationError[]) => void;
  handleScrollToFirstError: (errors: ValidationError[]) => void;
} => {
  const { setFocusedFieldId } = useFocusedField();

  const scrollToFirstError = useCallback(
    (errors: ValidationError[]) => {
      const sortedFieldsInStep = getSortedFields(form, {
        stepId: currentStepId,
      });

      const fieldWithError = sortedFieldsInStep?.find((_field) => {
        return errors.some(
          (error) => _field.target === error.errorPath.split('/')[0],
        );
      });

      setFocusedFieldId(fieldWithError?.id);
    },
    [currentStepId, form, setFocusedFieldId],
  );

  const handleScrollToFirstError = useCallback(
    (errors: ValidationError[]) => {
      const sortedFields = getSortedFields(form);
      const field = sortedFields?.find((_field) => {
        return errors.some(
          (error) => _field.target === error.errorPath.split('/')[0],
        );
      });

      if (Number(form.steps?.length) > 0) {
        const fieldStep = form.steps?.find((step) => {
          const fieldInStep = step.layout?.large?.items?.find(
            (layoutField) => layoutField.fieldId === field?.id,
          );
          return Boolean(fieldInStep);
        });
        const fieldStepIndex = fieldStep && form.steps?.indexOf(fieldStep);

        if (
          typeof fieldStepIndex !== 'undefined' &&
          fieldStep?.id !== currentStepId
        ) {
          handleStepChange(fieldStepIndex);
        }
      }

      if (errors.length > 0) {
        setFocusedFieldId(field?.id);
      }
    },
    [form, currentStepId, handleStepChange, setFocusedFieldId],
  );

  return { scrollToFirstError, handleScrollToFirstError };
};
